import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { noteApi } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Spinner } from 'reactstrap';

const CalendarSidebar = () => {
    const { t, i18n } = useTranslation();
    const [notesWithDates, setNotesWithDates] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [currentMonth, setCurrentMonth] = useState(new Date());

    useEffect(() => {
        fetchNotesForMonth(currentMonth);
    }, [currentMonth]);

    useEffect(() => {
        const handleRefresh = () => {
            // Dodajemy małe opóźnienie
            setTimeout(() => {
                fetchNotesForMonth(currentMonth);
            }, 500);
        };
        window.addEventListener('notes-updated', handleRefresh);
        return () => window.removeEventListener('notes-updated', handleRefresh);
    }, [currentMonth]);

    const fetchNotesForMonth = async (date) => {
        setLoading(true);
        try {
            // Obliczamy zakres dat dla widocznego miesiąca
            const year = date.getFullYear();
            const month = date.getMonth();
            
            const startDate = new Date(year, month, 1);
            const endDate = new Date(year, month + 1, 0, 23, 59, 59);

            const fromStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-01T00:00:00`;
            const toStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}T23:59:59`;

            const res = await noteApi.get('calendar', {
                params: {
                    from: fromStr,
                    to: toStr
                }
            });
            
            // note-core zwraca listę obiektów Note (nie opakowaną w content)
            setNotesWithDates(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Failed to fetch notes for calendar", err);
        } finally {
            setLoading(false);
        }
    };

    const handleActiveStartDateChange = ({ activeStartDate }) => {
        setCurrentMonth(activeStartDate);
    };

    const getTileClassName = ({ date, view }) => {
        if (view === 'month') {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateString = `${year}-${month}-${day}`;
            
            // Sprawdzamy czy którakolwiek notatka ma ten termin (ignorując czas)
            const notesOnDay = notesWithDates.filter(n => {
                if (!n.targetDate) return false;
                
                // Formatuje datę z notatki do YYYY-MM-DD
                let noteDateStr = "";
                if (Array.isArray(n.targetDate)) {
                    // Jeśli przyjdzie jako tablica [2026, 3, 19, ...]
                    const [y, m, d] = n.targetDate;
                    noteDateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                } else if (typeof n.targetDate === 'string') {
                    noteDateStr = n.targetDate.substring(0, 10);
                }
                
                return noteDateStr === dateString;
            });
            
            if (notesOnDay.length > 0) {
                const now = new Date();
                // Sprawdzamy czy którakolwiek notatka na ten dzień jest już przeterminowana
                // Parsujemy datę bezpiecznie dla porównania
                const isOverdue = notesOnDay.some(n => {
                    const tDate = Array.isArray(n.targetDate) 
                        ? new Date(n.targetDate[0], n.targetDate[1]-1, n.targetDate[2], n.targetDate[3]||0, n.targetDate[4]||0)
                        : new Date(n.targetDate);
                    return tDate < now;
                });
                return isOverdue ? 'bg-danger text-white rounded-circle' : 'bg-success text-white rounded-circle';
            }
        }
        return null;
    };

    const handleDateClick = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        // Navigate to search page with date filter
        // The search-engine expects yyyy-MM-dd'T'HH:mm
        const from = `${dateString}T00:00`;
        const to = `${dateString}T23:59`;
        navigate(`/advanced-search?targetFrom=${from}&targetTo=${to}`);
    };

    return (
        <Card className="border-0 shadow-sm rounded-4 mb-4 sticky-top" style={{ top: '140px', zIndex: 10 }}>
            <CardBody className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">{t('calendar')}</h5>
                    {loading && <Spinner size="sm" color="primary" />}
                </div>
                <div className="calendar-container">
                    <Calendar 
                        onActiveStartDateChange={handleActiveStartDateChange}
                        value={currentMonth}
                        tileClassName={getTileClassName}
                        onClickDay={handleDateClick}
                        locale={i18n.language === 'pl' ? 'pl-PL' : 'en-US'}
                    />
                </div>
                <div className="mt-3 small opacity-75">
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="badge bg-success p-1">●</span> {t('upcoming')}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-danger p-1">●</span> {t('overdue')}
                    </div>
                </div>
            </CardBody>
            <style>{`
                .calendar-container .react-calendar {
                    width: 100%;
                    border: none;
                    background: transparent;
                    font-family: inherit;
                }
                .calendar-container .react-calendar__tile--active {
                    background: var(--sb-primary)!important;
                    color: white!important;
                    border-radius: 50%;
                }
                .calendar-container .react-calendar__tile {
                    padding: 0.75em 0.5em;
                    font-size: 0.8rem;
                }
                .calendar-container .react-calendar__month-view__days__day--neighboringMonth {
                    opacity: 0.3;
                }
                /* Custom dots/backgrounds for marked days */
                .calendar-container .bg-success { background-color: #28a745 !important; }
                .calendar-container .bg-danger { background-color: #dc3545 !important; }
            `}</style>
        </Card>
    );
};

export default CalendarSidebar;
